import React, { useState, useEffect, Suspense } from 'react';
import { Switch, useLocation, Route } from 'react-router-dom';
import { ApolloRoute } from 'routes/ApolloRoutes';
import { SuperAdminLayout, Loader } from '@safelyq/base-feature';

// Lazy load components
const SuperAdminHome = React.lazy(() => import('@safelyq/business-admin').then(module => ({ default: module.SuperAdminHome })));
const ManageBusiness = React.lazy(() => import('features/admin-manage-business/ManageBusiness'));
const MarketingUser = React.lazy(() => import('features/marketing-user-count/src/MarketingUser/index'));
const Organization = React.lazy(() => import('features/admin-delete-organization/Organization'));
const BusinessCategories = React.lazy(() => import('features/admin-business-categories/BusinessCategories'));
const ApplicationUsers = React.lazy(() => import('features/super-admin-app-users'));
const ManagePaymentLogs = React.lazy(() => import('features/admin-payment_logs/ManagePaymentLogs'));
const PromptChange = React.lazy(() => import('features/admin-prompt-change'));
const PermanentClosure = React.lazy(() => import('features/admin-permanent-closure/src/PermanentClosure/index'));
const PosSystems = React.lazy(() => import('features/admin-pos-systems'));
const NotFound = React.lazy(() => import('components/NotFound'));

export const SuperAdminRoutes = ({ profile, data }) => {
    const { pathname } = useLocation();
    const [isSuperAdmin, setIsSuperAdmin] = useState(null);
    useEffect(() => {
        if (data && data.getUser && data.getUser.user) {
            setIsSuperAdmin(data.getUser.user.isSuperAdmin || false);
        }
    }, [data]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isSuperAdmin === null) {
                setIsSuperAdmin(false);
            }
        }, 2000);

        return () => clearTimeout(timeout);
    }, [isSuperAdmin]);

    if (isSuperAdmin === null) {
        return <Loader isFullScreen />;
    } else if (!isSuperAdmin) {
        return (
            <Route path='*' render={() => <Suspense fallback={<Loader isFullScreen />}><NotFound /></Suspense>} />
        );
    }

    const pathId = String(pathname).substring(pathname.lastIndexOf('/') + 1);
    const businessId = Number(pathId) ? parseInt(pathId) : 0;

    // Switch -> ApolloRoute -> PageWrapper -> SuperAdminLayout -> Suspense -> Page
    // Boundary sits below the layout so only the page suspends; written once here since every route renders its own layout (AdminRoutes hoists one AdminLayout above the Switch, so no wrapper needed there).
    const PageWrapper = ({ children }) => (
        <SuperAdminLayout businessId={businessId} {...profile}>
            <Suspense fallback={<Loader isFullScreen />}>
                {children}
            </Suspense>
        </SuperAdminLayout>
    );

    return (
        <Switch>
            <ApolloRoute secure exact path='/superadmin/home' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><SuperAdminHome /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/managebusiness/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><ManageBusiness /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/marketinguser/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><MarketingUser /></PageWrapper>}
            />

            <ApolloRoute secure exact path='/superadmin/appuser/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><ApplicationUsers /></PageWrapper>}
            />

            <ApolloRoute secure exact path='/superadmin/organization/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><Organization /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/categories/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><BusinessCategories /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/payment_logs/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><ManagePaymentLogs /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/edit_prompt/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><PromptChange /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/permanentclosure/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><PermanentClosure /></PageWrapper>}
            />
            <ApolloRoute secure exact path='/superadmin/pos-systems/' isNavbarHide={true} profile={profile}
                component={() => <PageWrapper><PosSystems /></PageWrapper>}
            />
            <Route path='*' render={() => <PageWrapper><NotFound /></PageWrapper>} />
        </Switch>
    )
} 